/**
 * Public-page interaction telemetry for pages that do not own a specialized funnel
 * tracker. Dedicated landing pages already emit section-aware click events;
 * this module fills the gap for /portal/*, /portal-guest/*, /install,
 * /success/*, tutorials, checkout/account, and future standard public pages.
 *
 * Privacy boundary: this records control labels and URLs only. It does not
 * read freeform input values, passwords, selected text, or page body content.
 */

import { trackEvent } from './telemetry';

const MAX_LABEL_LENGTH = 120;
const DEDUPE_MS = 450;

let lastInteractionKey = '';
let lastInteractionAt = 0;

function compactText(value: string | null | undefined, max = MAX_LABEL_LENGTH): string {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);
}

function elementFromTarget(target: EventTarget | null): HTMLElement | null {
  if (!target) return null;
  if (target instanceof HTMLElement) return target;
  const node = target as Node & { parentElement?: HTMLElement | null };
  return node.parentElement || null;
}

function isDedicatedLandingPath(pathname: string): boolean {
  const path = pathname || '/';
  if (path === '/' || path === '/download') return true;
  if (path === '/x' || path === '/x-learn-more-001') return true;
  if (path === '/youtube' || path === '/youtube-learn-more-001') return true;
  return path.startsWith('/landing/')
    || path.startsWith('/x-agentic-')
    || path.startsWith('/x-promo-code-')
    || path.startsWith('/youtube-promo-code-')
    || path.startsWith('/download-001-')
    || path.startsWith('/dev-promo-')
    || path.startsWith('/app-signups-');
}

function shouldTrackPath(pathname: string): boolean {
  const path = pathname || '/';
  if (path.startsWith('/admin')) return false;
  // These routes already emit richer landing_section_* and landing_click_* data.
  if (isDedicatedLandingPath(path)) return false;
  return true;
}

function surfaceForPath(pathname: string): string {
  const path = pathname || '/';
  if (path.startsWith('/portal')) return 'portal';
  if (path.startsWith('/account')) return 'account';
  if (path.startsWith('/install')) return 'install';
  if (path.startsWith('/success')) return 'success';
  if (path.startsWith('/checkout')) return 'checkout';
  if (path.startsWith('/tutorials')) return 'tutorials';
  if (path.startsWith('/legal') || path === '/terms' || path === '/privacy' || path === '/eula') return 'legal';
  return 'public-site';
}

function clickableLabel(element: HTMLElement): string {
  const explicit = compactText(
    element.getAttribute('data-replay-label')
    || element.getAttribute('data-analytics-label')
    || element.getAttribute('data-track-label')
    || element.getAttribute('aria-label')
    || element.getAttribute('title'),
  );
  if (explicit) return explicit;

  const id = element.getAttribute('id');
  if (id) {
    try {
      const label = document.querySelector(`label[for="${CSS.escape(id)}"]`);
      const labelText = compactText(label?.textContent);
      if (labelText) return labelText;
    } catch {
      // CSS.escape may be unavailable in older embedded browsers; fall through.
    }
  }

  const text = compactText(element.textContent);
  if (text) return text;

  const href = element instanceof HTMLAnchorElement ? element.href : '';
  if (href) return href;

  return compactText(element.getAttribute('name') || element.className || element.tagName.toLowerCase());
}

function dedupe(key: string): boolean {
  const now = Date.now();
  if (key === lastInteractionKey && now - lastInteractionAt < DEDUPE_MS) return true;
  lastInteractionKey = key;
  lastInteractionAt = now;
  return false;
}

function urlBits(href: string | null | undefined): Record<string, string> {
  const raw = String(href || '').trim();
  if (!raw) return {};
  try {
    const url = new URL(raw, window.location.href);
    return {
      href: url.href,
      target_origin: url.origin,
      target_path: url.pathname,
      target_hash: url.hash || '',
      target_search: url.search ? url.search.slice(0, 240) : '',
    };
  } catch {
    return { href: raw };
  }
}

function detailsForClick(event: MouseEvent, clickable: HTMLElement): Record<string, unknown> {
  const path = window.location.pathname || '/';
  const anchor = clickable.closest('a') as HTMLAnchorElement | null;
  const href = anchor?.href || clickable.getAttribute('href') || '';
  const label = clickableLabel(clickable) || 'click';
  return {
    surface: surfaceForPath(path),
    page_path: path,
    actual_url: window.location.href,
    click_label: label,
    click_tag: clickable.tagName.toLowerCase(),
    click_role: clickable.getAttribute('role') || undefined,
    click_id: clickable.getAttribute('id') || undefined,
    button_type: clickable instanceof HTMLButtonElement ? clickable.type || 'button' : undefined,
    mouse_button: event.button,
    meta_key: event.metaKey || undefined,
    ctrl_key: event.ctrlKey || undefined,
    shift_key: event.shiftKey || undefined,
    alt_key: event.altKey || undefined,
    ...urlBits(href),
  };
}

function detailsForControlChange(control: HTMLElement): Record<string, unknown> {
  const path = window.location.pathname || '/';
  const label = clickableLabel(control) || 'control';
  const input = control as HTMLInputElement;
  const select = control as HTMLSelectElement;
  const selectedOption = control instanceof HTMLSelectElement
    ? select.selectedOptions?.[0]
    : null;
  return {
    surface: surfaceForPath(path),
    page_path: path,
    actual_url: window.location.href,
    control_label: label,
    control_tag: control.tagName.toLowerCase(),
    control_role: control.getAttribute('role') || undefined,
    control_id: control.getAttribute('id') || undefined,
    control_type: input.type || undefined,
    checked: control instanceof HTMLInputElement && ['checkbox', 'radio'].includes(String(input.type || '').toLowerCase())
      ? input.checked
      : undefined,
    // For selects we only keep option metadata, never freeform text inputs.
    selected_option_label: selectedOption ? compactText(selectedOption.textContent, 80) : undefined,
    selected_option_value: control instanceof HTMLSelectElement ? compactText(select.value, 80) : undefined,
  };
}

export function installPublicInteractionTelemetry(): () => void {
  const onClick = (event: MouseEvent) => {
    const path = window.location.pathname || '/';
    if (!shouldTrackPath(path)) return;

    const target = elementFromTarget(event.target);
    const clickable = target?.closest?.('button,a,summary,[role="button"],[data-track-click],[data-analytics-click]') as HTMLElement | null;
    if (!clickable || clickable.closest('[data-skip-public-click-telemetry="true"]')) return;

    const details = detailsForClick(event, clickable);
    const key = `${details.page_path}:${details.click_tag}:${details.click_label}:${details.href || ''}`;
    if (dedupe(`click:${key}`)) return;

    trackEvent('site_interaction_click', details).catch(() => {});
  };

  const onChange = (event: Event) => {
    const path = window.location.pathname || '/';
    if (!shouldTrackPath(path)) return;

    const target = elementFromTarget(event.target);
    if (!(target instanceof HTMLSelectElement || target instanceof HTMLInputElement)) return;
    if (target.closest('[data-skip-public-click-telemetry="true"]')) return;

    const type = String((target as HTMLInputElement).type || '').toLowerCase();
    if (target instanceof HTMLInputElement && !['checkbox', 'radio'].includes(type)) return;

    const details = detailsForControlChange(target);
    const key = `${details.page_path}:${details.control_tag}:${details.control_label}:${details.selected_option_value || details.checked || ''}`;
    if (dedupe(`change:${key}`)) return;

    trackEvent('site_interaction_change', details).catch(() => {});
  };

  document.addEventListener('click', onClick, true);
  document.addEventListener('change', onChange, true);
  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('change', onChange, true);
  };
}
